import { FC, Dispatch, useState, useEffect } from "react";
import { UserStateInterface } from "../../../middleware/interfaces/user/user-state-interface";
import { UserDecisionInterface } from "../../../middleware/interfaces/user/user-decision-interface";
import { ProjectInterface } from "../../../middleware/interfaces/project/project-interface";
import { FormatDateFE } from "../../../helper/general/methods";
import { ProjectDeleteInterface } from "../../../middleware/interfaces/project/project-delete-interface";
import { ProjectDownloadInterface } from "../../../middleware/interfaces/project/project-download-interface";
import { PingServerWithLastActionDate } from "../../../helper/ui/methods";
import MenuButton from "../user/menu-button";

interface projectInfoProps {
  state: UserStateInterface;
  selectedProject: ProjectInterface;
  setSelectedProject: Dispatch<any>;
  setProjectShareDialogReveal: Dispatch<any>;
  setProjectEditDialogReveal: Dispatch<any>;
  dispatch: Dispatch<any>;
}

const ProjectInfo: FC<projectInfoProps> = ({
  state,
  selectedProject,
  setSelectedProject,
  setProjectShareDialogReveal,
  setProjectEditDialogReveal,
  dispatch,
}) => {
  const [openProject, setOpenProject] = useState(false);
  const [projectDeleteDialogReveal, setProjectDeleteDialogReveal] =
    useState(false);
  const [projectDownload, setProjectDownload] = useState(false);
  const [projectEdit, setProjectEdit] = useState(false);

  useEffect(() => {
    if (openProject === true) {
      const updatedUser = state.user;
      updatedUser!.openProject = selectedProject;
      dispatch({
        type: "UPDATE_USER",
        payload: updatedUser,
        response: "",
      });
      //PING SERVER TO UPDATE THE SESSION
      PingServerWithLastActionDate(updatedUser!);
      setOpenProject(false);
    }
  }, [openProject]);

  useEffect(() => {
    if (projectDeleteDialogReveal) {
      const projectDeleteData: ProjectDeleteInterface = {
        email: state.user?.email!,
        projectID: selectedProject!.projectID!,
        projectUserRole: selectedProject!.projectUserRole,
        type: "ProjectDeleteInterface",
      };
      const decision: UserDecisionInterface = {
        title: `Delete ${selectedProject.projectName}?`,
        message:
          "The project and everything inside it will be removed for everyone it is shared with. This cannot be undone.",
        confirmLabel: "Delete",
        decisionOptions: ["DELETE_PROJECT", "NO_ACTION"],
        result: "",
        data: projectDeleteData,
        type: "UserDecisionInterface",
      };
      dispatch({ type: "DECISION", decision: decision });
    }
    setProjectDeleteDialogReveal(false);
  }, [projectDeleteDialogReveal]);

  useEffect(() => {
    if (projectDownload === true) {
      const projectDownloadData: ProjectDownloadInterface = {
        email: state.user!.email,
        projectID: selectedProject!.projectID!,
        projectUserRole: selectedProject!.projectUserRole,
        type: "ProjectDownloadInterface",
      };
      const decision: UserDecisionInterface = {
        title: `Download ${selectedProject.projectName}?`,
        message:
          "The project is bundled in your browser and saved to your computer.",
        confirmLabel: "Download",
        decisionOptions: ["DOWNLOAD_PROJECT", "NO_ACTION"],
        result: "",
        data: projectDownloadData,
        type: "UserDecisionInterface",
      };
      dispatch({ type: "DECISION", decision: decision });
    }
    setProjectDownload(false);
  }, [projectDownload]);

  useEffect(() => {
    if (projectEdit === true) {
      setProjectEditDialogReveal("EDIT");
    }
    setProjectEdit(false);
  }, [projectEdit]);

  const FormatDateOrDash = (date?: string | null) => {
    if (!date) return "—";
    const time = new Date(date).getTime();
    if (isNaN(time) || time === 0) return "—";
    return FormatDateFE(date, true);
  };

  return (
    <>
      {(() => {
        if (selectedProject) {
          return (
            <div className="itemDetails">
              <div className="itemDetailsHeader">
                <img
                  className="icon-medium"
                  src="assets/icons/project.png"
                  draggable="false"
                  alt={selectedProject.projectName}
                />
                <div className="itemDetailsIdentity">
                  <span className="itemDetailsName">
                    {selectedProject.projectName}
                  </span>
                  <span className="itemDetailsSubtitle">
                    Project · {selectedProject.projectUserRole}
                  </span>
                </div>
                <span
                  className="itemDetailsClose"
                  onClick={() => {
                    setSelectedProject(null);
                  }}
                >
                  ×
                </span>
              </div>
              <div className="itemDetailsMetadata">
                <span className="itemDetailsLabel">LOCATION</span>
                <span className="itemDetailsValue">
                  {selectedProject.projectLocation || "—"}
                </span>
                <span className="itemDetailsLabel">START</span>
                <span className="itemDetailsValue">
                  {FormatDateOrDash(selectedProject.projectStartDate)}
                </span>
                <span className="itemDetailsLabel">END</span>
                <span className="itemDetailsValue">
                  {FormatDateOrDash(selectedProject.projectEndDate)}
                </span>
                <span className="itemDetailsLabel">CREATED</span>
                <span className="itemDetailsValue">
                  {FormatDateOrDash(selectedProject.projectCreationDate)}
                </span>
                <span className="itemDetailsLabel">OWNER</span>
                <span className="itemDetailsValue">
                  {selectedProject.email}
                </span>
                <span className="itemDetailsLabel">DESCRIPTION</span>
                <span className="itemDetailsValue">
                  {selectedProject.projectDescription || "—"}
                </span>
              </div>
              <div className="itemDetailsActions">
                <MenuButton
                  imageSource="assets/icons/enter.png"
                  alternativeText="Enter"
                  buttonSize="small"
                  buttonAccent="primary"
                  dispatch={() => {
                    setOpenProject(true);
                  }}
                />
                {selectedProject.projectUserRole !== "USER" ? (
                  <MenuButton
                    imageSource="assets/icons/share.png"
                    alternativeText="Share"
                    buttonSize="small"
                    dispatch={() => {
                      setProjectShareDialogReveal(true);
                    }}
                  />
                ) : (
                  <></>
                )}
                {selectedProject.projectUserRole !== "USER" ? (
                  <MenuButton
                    imageSource="assets/icons/team.png"
                    alternativeText="Team"
                    buttonSize="small"
                    dispatch={() => {
                      dispatch({
                        type: "RETRIEVE_PROJECT_TEAM",
                        payload: {
                          email: state.user!.email,
                          projectUserRole: selectedProject!.projectUserRole,
                          projectID: selectedProject!.projectID!,
                          type: "ProjectTeamRetrieveInterface",
                        },
                      });
                    }}
                  />
                ) : (
                  <></>
                )}
                <MenuButton
                  imageSource="assets/icons/download.png"
                  alternativeText="Download"
                  buttonSize="small"
                  dispatch={() => {
                    setProjectDownload(true);
                  }}
                />
                {selectedProject.projectUserRole !== "USER" ? (
                  <MenuButton
                    imageSource="assets/icons/edit.png"
                    alternativeText="Edit"
                    buttonSize="small"
                    dispatch={() => {
                      setProjectEdit(true);
                    }}
                  />
                ) : (
                  <></>
                )}
                <MenuButton
                  imageSource="assets/icons/delete.png"
                  alternativeText="Delete"
                  buttonSize="small"
                  buttonAccent="destructive"
                  dispatch={() => {
                    setProjectDeleteDialogReveal(true);
                  }}
                />
              </div>
            </div>
          );
        } else {
          return <></>;
        }
      })()}
    </>
  );
};

export default ProjectInfo;
